/**
 * /login route. Public, sits OUTSIDE the Layout's AuthGate.
 *
 * Three explicit, equal sign-in options. Any Microsoft or Google account is
 * accepted at the identity layer; the database (via /v1/core/me) decides what
 * the signed-in principal can actually access. There is no email-domain
 * routing: the user picks a method rather than the page picking one for them.
 *
 *   1. Sign in with Microsoft -> Microsoft Entra OIDC (any tenant).
 *   2. Sign in with Google    -> Google Workspace OIDC (any domain).
 *   3. Sign in with an email code -> POST /v1/core/auth/email/start emails a
 *      6-digit code, then POST /v1/core/auth/email/verify sets the session
 *      cookie and the page navigates to the post-login target.
 *
 * All three set the same session cookie on the shared Ampersand API platform.
 *
 * Copied from the cockpit UI's pages/Login.tsx (same auth flow, same shared
 * API platform), with the branding swapped to The Sweet Spot.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getEntraLoginUrl, getGoogleLoginUrl } from '@/lib/useSession';
import { apiPost, API_BASE, type ApiError } from '@/lib/api';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import logoUrl from '@/assets/logo.png';
import logoDarkUrl from '@/assets/logo-dark.png';

function safeReturn(raw: string | null): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '/';
  return raw;
}

// Client-side sanity check before hitting the API. Deliberately loose: it
// catches obvious typos (missing @, missing domain) without trying to fully
// validate the address.
function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function Login() {
  useDocumentTitle();
  const [params] = useSearchParams();
  const returnTo = safeReturn(params.get('return'));

  // Email one-time-code flow state. `codeSent` flips the email-code option from
  // the "send a code" form to the code entry form.
  const [email, setEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  async function sendCode(e?: React.FormEvent) {
    e?.preventDefault();
    const trimmed = email.trim();
    setCodeError(null);
    if (!isValidEmail(trimmed)) {
      setCodeError('Enter a valid email address first.');
      return;
    }
    setSending(true);
    try {
      await apiPost(`${API_BASE}/v1/core/auth/email/start`, { email: trimmed });
      setCode('');
      setCodeSent(true);
    } catch (err) {
      const status = (err as ApiError)?.status;
      if (status === 429) {
        setCodeError('Too many requests. Wait a moment, then try again.');
      } else {
        setCodeError('Could not send a code to that email. Check the address and try again.');
      }
    } finally {
      setSending(false);
    }
  }

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    const entered = code.trim();
    setCodeError(null);
    if (entered.length !== 6) {
      setCodeError('Enter the 6-digit code from your email.');
      return;
    }
    setVerifying(true);
    try {
      await apiPost(`${API_BASE}/v1/core/auth/email/verify`, { email: trimmed, code: entered });
      // The API set the session cookie. Reload into the app at the post-login
      // target; AuthGate then sees the cookie via /v1/core/me.
      window.location.assign(returnTo);
    } catch (err) {
      const status = (err as ApiError)?.status;
      if (status === 429) {
        setCodeError('Too many attempts. Request a new code and try again.');
      } else if (status === 400 || status === 401 || status === 422) {
        setCodeError('That code is incorrect or has expired. Request a new one.');
      } else {
        setCodeError('Could not verify the code. Try again.');
      }
    } finally {
      setVerifying(false);
    }
  }

  const emailEmpty = email.trim().length === 0;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-candy-cream px-4 dark:bg-candy-dark-bg">
      <div className="w-full max-w-md rounded-lg border border-candy-line bg-candy-white p-8 shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
        <span className="mx-auto mb-4 flex w-fit items-center">
          <img src={logoUrl} alt="The Sweet Spot" className="h-24 w-auto dark:hidden" />
          <img src={logoDarkUrl} alt="" aria-hidden className="hidden h-20 w-auto dark:block" />
        </span>
        <h1 className="mb-6 text-center text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">
          Sign in
        </h1>

        <div className="flex flex-col gap-2">
          <a
            href={getEntraLoginUrl(returnTo)}
            className="inline-flex w-full items-center justify-center rounded-md border border-candy-line bg-candy-white px-4 py-2 text-sm font-medium text-candy-ink hover:border-candy-pink hover:bg-candy-cream dark:border-candy-dark-line dark:bg-candy-dark-surface dark:text-candy-dark-ink dark:hover:border-candy-pink-dark dark:hover:bg-candy-dark-surface-warm"
          >
            Sign in with Microsoft
          </a>
          <a
            href={getGoogleLoginUrl(undefined, returnTo)}
            className="inline-flex w-full items-center justify-center rounded-md border border-candy-line bg-candy-white px-4 py-2 text-sm font-medium text-candy-ink hover:border-candy-pink hover:bg-candy-cream dark:border-candy-dark-line dark:bg-candy-dark-surface dark:text-candy-dark-ink dark:hover:border-candy-pink-dark dark:hover:bg-candy-dark-surface-warm"
          >
            Sign in with Google
          </a>
        </div>

        <div className="my-5 flex items-center gap-3 text-xs text-candy-ink-muted dark:text-candy-dark-ink-muted">
          <span className="h-px flex-1 bg-candy-line dark:bg-candy-dark-line" />
          or
          <span className="h-px flex-1 bg-candy-line dark:bg-candy-dark-line" />
        </div>

        {codeSent ? (
          <form
            onSubmit={verifyCode}
            className="space-y-2 rounded-md border border-candy-line p-3 dark:border-candy-dark-line"
          >
            <label
              htmlFor="login-code"
              className="block text-sm font-medium text-candy-ink dark:text-candy-dark-ink"
            >
              Enter the 6-digit code sent to {email.trim()}
            </label>
            <input
              id="login-code"
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="123456"
              value={code}
              onChange={(ev) => {
                setCode(ev.target.value.replace(/\D/g, '').slice(0, 6));
                if (codeError) setCodeError(null);
              }}
              className="w-full rounded-md border border-candy-line bg-candy-white px-3 py-2 text-sm tracking-widest text-candy-ink placeholder:text-candy-ink-muted focus:border-candy-pink focus:outline-none dark:border-candy-dark-line dark:bg-candy-dark-surface dark:text-candy-dark-ink dark:placeholder:text-candy-dark-ink-muted"
            />
            <button
              type="submit"
              disabled={verifying || code.length !== 6}
              className="inline-flex w-full items-center justify-center rounded-md bg-candy-pink px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 dark:bg-candy-pink-dark dark:text-candy-dark-bg"
            >
              {verifying ? 'Verifying...' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={() => sendCode()}
              disabled={sending}
              className="inline-flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-candy-ink-muted hover:text-candy-ink disabled:opacity-50 dark:text-candy-dark-ink-muted dark:hover:text-candy-dark-ink"
            >
              {sending ? 'Sending...' : 'Resend code'}
            </button>
          </form>
        ) : (
          <form onSubmit={sendCode} className="space-y-2">
            <label
              htmlFor="login-email"
              className="block text-sm font-medium text-candy-ink dark:text-candy-dark-ink"
            >
              Sign in with an email code
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder="you@example.com"
              value={email}
              onChange={(ev) => {
                setEmail(ev.target.value);
                if (codeError) setCodeError(null);
              }}
              className="w-full rounded-md border border-candy-line bg-candy-white px-3 py-2 text-sm text-candy-ink placeholder:text-candy-ink-muted focus:border-candy-pink focus:outline-none dark:border-candy-dark-line dark:bg-candy-dark-surface dark:text-candy-dark-ink dark:placeholder:text-candy-dark-ink-muted"
            />
            <button
              type="submit"
              disabled={sending || emailEmpty}
              className="inline-flex w-full items-center justify-center rounded-md border border-candy-line bg-candy-white px-4 py-2 text-sm font-medium text-candy-ink hover:border-candy-pink hover:bg-candy-cream disabled:opacity-50 dark:border-candy-dark-line dark:bg-candy-dark-surface dark:text-candy-dark-ink dark:hover:border-candy-pink-dark dark:hover:bg-candy-dark-surface-warm"
            >
              {sending ? 'Sending...' : 'Email me a sign-in code'}
            </button>
          </form>
        )}

        {codeError ? (
          <p className="mt-2 text-sm text-red-700 dark:text-red-300">{codeError}</p>
        ) : null}

        <p className="mt-5 text-center text-xs text-candy-ink-muted dark:text-candy-dark-ink-muted">
          Sign in with your Microsoft or Google account, or have a one-time code emailed to you.
        </p>
      </div>
    </div>
  );
}
